import React, { useEffect, useRef, useState } from 'react'
import { Close8, Preference } from '@app/images'
import { Download, Unsign, Pencil, Delete } from '@app/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { contractsActions, userSelectors } from '@/entity'
import { Field, Button, Checkbox } from '@/ui'
import { Drawer } from 'antd'
import { Formik, Form, Field as FormikField } from 'formik'
import cl from 'classnames'
import styles from './contraxtsSearch.module.scss'
import { ExportModal } from '@features/contracts/exportModal'

const FORM_NAME = {
	bin: 'bin',
	iin: 'iin',
	surname: 'surname',
	firstname: 'firstname',
	patronname: 'patronname',
	companyName: 'companyName',
	contractNumber: 'contractNumber',
	establishedPost: 'establishedPost',
	dcontractStatusCode: 'dcontractStatusCode',
	dcontractStateCode: 'dcontractStateCode',
	dcontractTypeCode: 'dcontractTypeCode',
	registerDate: 'registerDate',
	dateFrom: 'dateFrom',
	dateTo: 'dateTo',
	terminationDate: 'terminationDate',
	dsendStatusCode: 'dsendStatusCode',
	representative: 'representative',
	srcText: 'srcText',
}

let ROLES = {
	ROLE_EMPLOYEE: 'ROLE_EMPLOYEE',
	ROLE_EMPLOYER: 'ROLE_EMPLOYER',
}

const modalType = {
	IMPORT: 'IMPORT'
}

const modalInitialState = {
	item: null,
	type: {
		[modalType.IMPORT]: false,
	},
}

const colors = ['#FFBC34', '#E65332']

const ContractSearch = ({fetchData, filters}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const searchFormRef = useRef(null)

	const { role, statusList, stateList, typeList, sendStatuses } = useSelector((state) => ({
		role: userSelectors.activeRole(state),
		statusList: state.handbook.contractStatuses,
		stateList: state.handbook.contractStates,
		typeList: state.handbook.contractTypes,
		sendStatuses: state.handbook.sendStatuses.map((item, index) => ({
			...item,
			icon: <Unsign fill={colors[index]} />,
		}))
	}))

	const [modalData, setModalData] = useState(modalInitialState)

	const [isAdvancedMenu, setIsAdvancedMenu] = useState(false)

	const handleSearch = (data) => {
		fetchData(data)
	}

	const onReset = () => {
		dispatch(contractsActions.setSelectedContracts([]))
		fetchData()
	}

	useEffect(() => {
		if(isAdvancedMenu){
			const drawer = document.querySelector(".ant-drawer-body")
			drawer.classList.add('scroll')
		}
	}, [isAdvancedMenu])

	return (
		<>
			<div className={cl(styles.searchRow)}>
				<Formik initialValues={{}} onSubmit={handleSearch} innerRef={searchFormRef}>
					{({ values, handleReset }) => {
						return (
							<Form>

								<div className={cl('flex', 'gap-3', 'flex-wrap', 'flex-column', 'flex-xxl-row', 'flex-column')}>
									<div className={cl('flex', 'gap-3', 'flex-1')}>
										{
											role === ROLES.ROLE_EMPLOYER ?
												(
													<>
														<div style={{width: 200}}>
															<div>
																<FormikField name={FORM_NAME.iin}>
																	{({ field, meta: { touched, error } }) => (
																		<Field
																			{...field}
																			type="number"
																			min={0}
																			hideNumberArrows
																			label={t('iin')}
																			placeholder={t('enter_12_digits')}
																			onChange={(value) => {
																				if (value.length > 12) value = value.slice(0, 12)
																				field.onChange(value)
																			}}
																		/>
																	)}
																</FormikField>
															</div>
														</div>
														<div className={cl('flex', 'flex-2', 'gap-3', 'flex-nowrap')}>
															<div className={cl('flex-1')}>
																<FormikField name={FORM_NAME.surname}>
																	{({ field, meta: { touched, error } }) => (
																		<Field
																			type="text"
																			label={t('lastname')}
																			placeholder={t('accidentsPage.placeholderSurname')}
																			{...field}
																		/>
																	)}
																</FormikField>
															</div>

															<div className={cl('flex-1')}>
																<FormikField name={FORM_NAME.firstname}>
																	{({ field, meta: { touched, error } }) => (
																		<Field
																			type="text"
																			label={t('firstname')}
																			placeholder={t('accidentsPage.placeholderFirstname')}
																			{...field}
																		/>
																	)}
																</FormikField>
															</div>
															<div className={cl('flex-1')}>
																<FormikField name={FORM_NAME.patronname}>
																	{({ field, meta: { touched, error } }) => (
																		<Field
																			type="text"
																			label={t('middlename')}
																			placeholder={t('accidentsPage.placeholderSecondName')}
																			{...field}
																		/>
																	)}
																</FormikField>
															</div>
														</div>
													</>
												) : (
													<>
														<div style={{ width: 200 }}>
															<FormikField name={FORM_NAME.bin}>
																{({ field, meta: { touched, error } }) => (
																	<Field
																		{...field}
																		type="number"
																		hideNumberArrows
																		label={t('bin')}
																		placeholder={t('enter_12_digits')}
																		onChange={(value) => {
																			if (value.length > 12) value = value.slice(0, 12)
																			field.onChange(value)
																		}}
																	/>
																)}
															</FormikField>
														</div>
														<div className={cl('flex-1')}>
															<FormikField name={FORM_NAME.companyName}>
																{({ field, meta: { touched, error } }) => (
																	<Field
																		type="text"
																		label={t('history_check.business_name')}
																		placeholder={t('labor_records.orgName')}
																		{...field}
																	/>
																)}
															</FormikField>
														</div>
													</>
												)
										}
									</div>

									<div className={cl('flex', 'align-center', 'gap-4', 'pt-0', 'pt-xxl-4')}>
										<Button type="bare" onClick={() => {
											handleReset()
											onReset()
										}}>
											{t('reset_all')}
										</Button>

										<Button onClick={() => handleSearch(values)}>
											{t('search')}
										</Button>

										<div style={{ cursor: 'pointer' }} className={cl('flex', 'align-center', 'gap-3')} onClick={() => {
											setIsAdvancedMenu(true)
										}}>
											<img src={Preference} />
											{t('contracts_tab.advancedSearch')}
										</div>

										<div style={{ cursor: 'pointer' }} className={cl('flex', 'align-center', 'gap-3')} onClick={() => {
											setModalData(prevState => ({
												...prevState,
												type: {
													[modalType.IMPORT]: true
												}
											}))
										}}>
											<Download />
											{t('labor_records.export')}
										</div>
									</div>
								</div>

								<Drawer onClose={() => setIsAdvancedMenu(false)} title={t('contracts_tab.advancedSearch')}
												open={isAdvancedMenu} closeIcon={false} extra={(
									<div className={cl('flex', 'justify-content-between')}>
										<img style={{ cursor: 'pointer' }} src={Close8} alt="close" width={20} height={20} onClick={() => {
											setIsAdvancedMenu(false)
										}} />
									</div>
								)} footer={(
									<div className={cl('flex', 'gap-4', 'align-center', 'justify-content-end')}>
										<div>
											<Button type="bare" onClick={handleReset}>
												{t('reset_all')}
											</Button>
										</div>

										<div>
											<Button onClick={() => handleSearch(values)}>
												{t('search')}
											</Button>
										</div>
									</div>
								)}>
									<div className={cl('flex', 'flex-column', 'gap-4')}>
										<FormikField name={FORM_NAME.contractNumber}>
											{({ field, meta: { touched, error } }) => (
												<Field
													type="number"
													min={0}
													hideNumberArrows
													label={t('contracts_tab.contract_number')}
													placeholder={t('enter_number')}
													{...field}
												/>
											)}
										</FormikField>

										<FormikField name={FORM_NAME.establishedPost}>
											{({ field, meta: { touched, error } }) => (
												<Field
													type="text"
													label={t('contract.info.position')}
													placeholder={t('enter_position')}
													labelClassName={cl('flex')}
													{...field}
												/>
											)}
										</FormikField>

										<FormikField name={FORM_NAME.dcontractStatusCode}>
											{({ field, meta: { touched, error } }) => (
												<Field
													label={t('personal_affairs.status')}
													placeholder={t('select_from_list')}
													options={statusList}
													type="text"
													fieldType="selectSearch"
													{...field}
												/>
											)}
										</FormikField>

										<FormikField name={FORM_NAME.dcontractStateCode}>
											{({ field, meta: { touched, error } }) => (
												<Field
													label={t('pension.state')}
													placeholder={t('select_from_list')}
													options={stateList}
													type="text"
													fieldType="selectSearch"
													{...field}
												/>
											)}
										</FormikField>

										<FormikField name={FORM_NAME.dcontractTypeCode}>
											{({ field, meta: { touched, error } }) => (
												<Field
													label={t('contracts_tab.type')}
													placeholder={t('select_from_list')}
													options={typeList}
													type="text"
													fieldType="selectSearch"
													{...field}
												/>
											)}
										</FormikField>

										<FormikField name={FORM_NAME.registerDate}>
											{({ field, meta: { touched, error } }) => (
												<Field
													fieldType="datePicker"
													label={t('esutd_reg_date')}
													placeholder={t('default_date_value')}
													{...field}
												/>
											)}
										</FormikField>

										<FormikField name={FORM_NAME.dateFrom}>
											{({ field, meta: { touched, error } }) => (
												<Field
													fieldType="datePicker"
													label={t('contracts_tab.conclusion_date')}
													placeholder={t('default_date_value')}
													{...field}
												/>
											)}
										</FormikField>

										<FormikField name={FORM_NAME.dateTo}>
											{({ field, meta: { touched, error } }) => (
												<Field
													fieldType="datePicker"
													label={t('contracts_tab.expired_date')}
													placeholder={t('default_date_value')}
													{...field}
												/>
											)}
										</FormikField>

										<FormikField name={FORM_NAME.terminationDate}>
											{({ field, meta: { touched, error } }) => (
												<Field
													fieldType="datePicker"
													label={t('terminate_date_contract')}
													placeholder={t('default_date_value')}
													{...field}
												/>
											)}
										</FormikField>

										<FormikField name={FORM_NAME.dsendStatusCode}>
											{({ field, meta: { touched, error } }) => (
												<Field
													label={t('sign_subscription')}
													placeholder={t('select_from_list')}
													options={sendStatuses}
													type="text"
													fieldType="selectSearch"
													{...field}
												/>
											)}
										</FormikField>

										{
											role === ROLES.ROLE_EMPLOYEE ?
												<FormikField name={FORM_NAME.representative}>
													{({ field, meta: { touched, error } }) => (
														<Checkbox
															name={FORM_NAME.representative}
															label={t('isChildren')}
															{...field}
														/>
													)}
												</FormikField> : null
										}
									</div>
								</Drawer>
							</Form>
						)
					}}
				</Formik>
			</div>

			{modalData.type[modalType.IMPORT] && <ExportModal filters={filters} handleCancel={() => setModalData(modalInitialState)} />}
		</>
	)
}

export default ContractSearch
